import type { ScaleLinear, ScaleLogarithmic, ScaleSymLog } from 'd3-scale';

import type { ThresholdMarker } from '../components/threshold-bar/ThresholdBar.js';
import { getMarkerValue } from '../components/threshold-bar/utils/get-marker-value.js';
import { getThresholdPixel } from '../components/threshold-bar/utils/get-threshold-pixel.ts.js';
import type { FormatterWithPrecision } from '../types/formatter.js';
import type {
  DateThresholdConfig,
  NumericalThresholdConfig,
  ThresholdPosition,
} from '../types/thresholds.js';
import {
  isDynamic,
  isDynamicNumeric,
  isDynamicNumericRange,
  isDynamicRange,
  isStatic,
  isStaticRange,
} from '../types/type-guards.js';
import type { WithId } from '../types/with-id.js';

export const transformThresholdsToMarkers = (
  thresholds: WithId<DateThresholdConfig | NumericalThresholdConfig>[],
  position: ThresholdPosition,
  yScale:
    | ScaleLinear<number, number>
    | ScaleSymLog<number, number>
    | ScaleLogarithmic<number, number>,
  formatter: FormatterWithPrecision,
) =>
  thresholds.map(({ data, color, id, label }): ThresholdMarker => {
    if (isStatic(data)) {
      return {
        id,
        color,
        y0: getThresholdPixel(data.value, yScale),
        y1: getThresholdPixel(data.value, yScale),
        label,
        value: getMarkerValue({ y0: data.value, y1: data.value }, formatter),
      };
    } else if (isStaticRange(data)) {
      return {
        id,
        color,
        y0: getThresholdPixel(data.min, yScale),
        y1: getThresholdPixel(data.max, yScale),
        label,
        value: getMarkerValue({ y0: data.min, y1: data.max }, formatter),
      };
    } else if (isDynamic(data) || isDynamicNumeric(data)) {
      const dataValue =
        position === 'left' ? data[0].value : data[data.length - 1].value;

      return {
        id,
        color,
        y0: getThresholdPixel(dataValue, yScale),
        y1: getThresholdPixel(dataValue, yScale),
        label,
        value: getMarkerValue({ y0: dataValue, y1: dataValue }, formatter),
      };
    } else if (isDynamicRange(data) || isDynamicNumericRange(data)) {
      const [dataY0] = data;
      const dataY1 = data[data.length - 1];

      const maxValue = position === 'left' ? dataY0.max : dataY1.max;
      const minValue = position === 'left' ? dataY0.min : dataY1.min;

      return {
        id,
        color,
        y0: getThresholdPixel(minValue, yScale),
        y1: getThresholdPixel(maxValue, yScale),
        label,
        value: getMarkerValue({ y0: minValue, y1: maxValue }, formatter),
      };
    }

    return {
      id,
      color,
      y0: 0,
      y1: 0,
      label,
      value: getMarkerValue({ y0: 0, y1: 0 }, formatter),
    };
  });
